import { useCallback, useEffect, useRef } from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { useQuery } from '@tanstack/react-query';

import { useRestaurantsService } from '@restworld/data-services';
import { useAnalytics } from '@restworld/utils-common';
import { Restaurant } from '@restworld/utility-types';

import { isOrgOnboardingModeAtom } from '../../organization-onboarding/use-organization-onboarding';

import { activeRestaurantOnboardingStateAtom } from './restaurant-onboarding-atoms';
import { activeAddressIdAtom, activeRestaurantAtom } from './google-address-input-result';

export const useRestaurantInformationOnboardingQueryKey = () => {
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const activeRestaurant = useAtomValue(activeRestaurantAtom);
  return [`restaurant-onboarding-data-${activeAddressId}-${activeRestaurant}`];
};
export const restaurantInformationDataAtom = atom<Partial<Restaurant>>({});

/**
 * One automatic retry, and only when the request never got an answer (no HTTP status: offline,
 * dropped connection, timeout). Every request runs a ~10 s backend pipeline (Google, OpenAI,
 * Bedrock), so retrying an error answer (401, 5xx) only makes the employer wait longer for the
 * same result. After that the loader shows an error with a button to try again.
 */
const MAX_NETWORK_RETRIES = 1;

const lookupHttpStatus = (error: unknown): number | null => {
  const code = (error as { code?: unknown } | null)?.['code'];
  return typeof code === 'number' ? code : null;
};

/**
 * 409 (venue already registered) and 400 not_horeca are expected ends of a lookup, each with its
 * own screen, not failures: they are never retried, logged or shown as an error.
 */
export const isExpectedLookupOutcome = (error: unknown): boolean => {
  const status = lookupHttpStatus(error);
  return status === 409 || (status === 400 && error?.['data']?.error === 'not_horeca');
};

type EnrichmentOutcome = 'success' | 'not_horeca' | 'already_exists' | 'error' | 'network_error' | 'abandoned';

/**
 * One `employer_onboarding_venue_enrichment_completed` per venue lookup on /getting-started,
 * sent when the lookup is over: on success, on an answer that ends it (409 already exists,
 * not_horeca) or after the last retry. `duration_ms` runs from the first request to that
 * moment, retries included, because that is how long the employer looks at the loader.
 * `not_horeca` is its own outcome: the backend answers it with a 400, but it is the expected
 * end of a lookup for a business outside hospitality, not a failure.
 *
 * `abandoned` is sent when the loader goes away with the lookup still open, during a request
 * or between retries (back button, a move inside the app). Closing the tab or reloading does
 * not unmount anything, so that exit sends nothing: a `magic_ai_loading` pageview with no
 * outcome after it is that case.
 *
 * A lookup that has been reported is over, so the next request, even for the same venue
 * picked again after an error, starts a new one and gets its own event.
 */
const useEnrichmentOutcomeTracking = () => {
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);
  const { trackEvent } = useAnalytics();
  const lookupRef = useRef<{ placeId: string; startedAt: number; attempts: number; reported: boolean } | null>(null);

  const recordAttempt = useCallback((placeId: string) => {
    const lookup = lookupRef.current;
    if (!lookup || lookup.placeId !== placeId || lookup.reported) {
      lookupRef.current = { placeId, startedAt: performance.now(), attempts: 0, reported: false };
    }
    lookupRef.current.attempts += 1;
  }, []);

  const reportOutcome = useCallback(
    (outcome: EnrichmentOutcome, httpStatus: number | null) => {
      const lookup = lookupRef.current;
      if (!isOrgOnboarding || !lookup || lookup.reported) return;
      lookup.reported = true;
      trackEvent('employer_onboarding_venue_enrichment_completed', {
        outcome,
        http_status: httpStatus,
        duration_ms: Math.round(performance.now() - lookup.startedAt),
        attempts: lookup.attempts,
        gmaps_place_id: lookup.placeId,
      });
    },
    [isOrgOnboarding, trackEvent]
  );

  // Read through a ref so the unmount sees the latest callback without re-running the effect:
  // a cleanup on every change of `reportOutcome` would report a lookup still in progress.
  const reportOutcomeRef = useRef(reportOutcome);
  reportOutcomeRef.current = reportOutcome;
  useEffect(() => () => reportOutcomeRef.current('abandoned', null), []);

  return { recordAttempt, reportOutcome };
};

const useRestaurantInformationOnboardingDataQuery = () => {
  const restaurantServices = useRestaurantsService();
  const setActiveUI = useSetAtom(activeRestaurantOnboardingStateAtom);
  const activeAddressId = useAtomValue(activeAddressIdAtom);
  const activeRestaurant = useAtomValue(activeRestaurantAtom);
  const setActiveRestaurant = useSetAtom(activeRestaurantAtom);
  const setRestaurantInformationData = useSetAtom(restaurantInformationDataAtom);
  const queryKey = useRestaurantInformationOnboardingQueryKey();
  const { recordAttempt, reportOutcome } = useEnrichmentOutcomeTracking();

  const queryFn = useCallback(
    async ({ signal }) => {
      recordAttempt(activeAddressId);
      const data = (
        await restaurantServices.getAdditionalRestaurantOnboardingData({
          restaurant: activeRestaurant || undefined,
          gmaps_place_id: activeAddressId,
        })
      ).data;
      // Reading `signal` makes TanStack cancel the lookup when the loader unmounts (back, a new
      // search). Without this check a late answer would still open the form, with this venue's
      // data, on top of whatever screen the employer has moved to. The unmount has already
      // reported the lookup as `abandoned`, so nothing is reported here.
      if (signal?.aborted) return data;
      setRestaurantInformationData(data);
      setActiveUI('restaurant_onboarding_form');

      // When restaurant name wasn't provided (URL param flow), derive from API response
      // so it's available for form payload formatting (business_address field)
      if (!activeRestaurant && data?.restaurant_name) {
        const name = data.restaurant_name as string;
        const addr = (data.address as string) || '';
        setActiveRestaurant(addr ? `${name}, ${addr}` : name);
      }

      reportOutcome('success', 200);
      return data;
    },
    [
      activeAddressId,
      activeRestaurant,
      restaurantServices,
      setActiveUI,
      setActiveRestaurant,
      setRestaurantInformationData,
      recordAttempt,
      reportOutcome,
    ]
  );

  const query = useQuery({
    queryKey: queryKey,
    queryFn: queryFn,
    retry(failureCount, error) {
      const httpStatus = lookupHttpStatus(error);
      if (error?.['code'] === 409) {
        reportOutcome('already_exists', 409);
        const conflict = error?.['data'] || {};
        setRestaurantInformationData({
          restaurant_name: conflict.restaurant_name,
          locality: conflict.locality,
          organization_id: conflict.organization_id,
        });
        setActiveUI('restaurant_already_exists');
        return false;
      }
      if (error?.['code'] === 400 && error?.['data']?.error === 'not_horeca') {
        reportOutcome('not_horeca', 400);
        setActiveUI('not_horeca');
        return false;
      }
      console.error(`Error fetching restaurant onboarding data: ${error}`);
      const willRetry = httpStatus === null && failureCount < MAX_NETWORK_RETRIES;
      if (!willRetry) reportOutcome(httpStatus ? 'error' : 'network_error', httpStatus);
      return willRetry;
    },
    retryDelay: 1000,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchOnMount: false,
    staleTime: Infinity,
    enabled: !!activeAddressId,
  });

  return query;
};

export const useRestaurantOnboardingData = () => {
  const restaurantInformationData = useAtomValue(restaurantInformationDataAtom);
  return restaurantInformationData;
};

export default useRestaurantInformationOnboardingDataQuery;
